import type { BiLogger } from '@wix/wix-bi-logger-client';
import {
  wixCliCliCommand,
  wixCliCliCommandStatus,
} from '@wix/bi-logger-dev-tools-data/v2';
import { isCliError, isCliSystemError } from '@wix/cli-error';
import { uptime } from 'node:process';
import { getEnvironmentInfo } from './environment.js';
import type { Flow } from './types.js';

interface CommandLike {
  name: () => string;
  args: string[];
  opts: () => Record<string, unknown>;
}

interface CommandStatusFields {
  msid?: string;
  appId?: string;
}

const getDuration = () => Math.round(uptime() * 1000);

export function reportCommandStartEvent({
  biLogger,
  command,
  flow,
  cliVersion,
  projectId,
  msid,
  appId,
  extensions,
  extraFields,
}: {
  biLogger: BiLogger;
  command: CommandLike;
  flow: Flow | null;
  cliVersion: string;
  projectId?: string;
  msid?: string;
  appId?: string;
  extensions?: Record<string, number>;
  extraFields?: Record<string, unknown>;
}) {
  const options = command.opts();
  const args = command.args;

  const privateOptions = new Set(['apiKey', 'refreshToken']);
  const sanitizedOptions = Object.fromEntries(
    Object.entries(options).map(([flagName, flagValue]) => {
      const value = privateOptions.has(flagName) ? '<censored>' : flagValue;
      return [flagName, value];
    })
  );

  const commandFields = {
    command: command.name(),
    arguments: args.join(','),
    flags: Object.keys(sanitizedOptions).length
      ? JSON.stringify(sanitizedOptions)
      : '',
  };

  void biLogger.report(
    wixCliCliCommand({
      ...getEnvironmentInfo({ cliVersion, flow }),
      ...commandFields,
      ...(projectId ? { projectId } : {}),
      ...(extraFields ? { extraFields: JSON.stringify(extraFields) } : {}),
      ...(msid ? { _msid: msid } : {}),
      ...(appId ? { _appId: appId } : {}),
      isValid: true,
    })
  );

  return {
    succeed(fields?: CommandStatusFields) {
      const _msid = fields?.msid ?? msid;
      const _appId = fields?.appId ?? appId;
      void biLogger.report(
        wixCliCliCommandStatus({
          ...commandFields,
          ...(extensions ? { extensions: JSON.stringify(extensions) } : {}),
          duration: getDuration(),
          ...(_msid ? { _msid } : {}),
          ...(_appId ? { _appId } : {}),
          isSuccess: true,
          isValid: true,
        })
      );
    },
    failed(error: unknown) {
      const errorFields = isCliError(error)
        ? {
            errorType: isCliSystemError(error) ? 'SystemError' : 'UserError',
            errorName: error.name,
            errorMessage: error.message,
          }
        : error instanceof Error
          ? {
              errorType: 'UnknownError',
              errorName: error.name,
              errorMessage: error.message,
            }
          : {
              errorType: 'UnknownError',
              errorMessage: JSON.stringify(error),
            };

      void biLogger.report(
        wixCliCliCommandStatus({
          ...commandFields,
          ...errorFields,
          ...(extensions ? { extensions: JSON.stringify(extensions) } : {}),
          duration: getDuration(),
          ...(msid ? { _msid: msid } : {}),
          isSuccess: false,
          isValid: true,
        })
      );
    },
  };
}
